import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Grid,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Stack,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Tooltip,
	CircularProgress,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useLocation, useNavigate } from "react-router-dom";
import "./DiscountSummary.css";
import { formatSchemeText, formatSchemeTypeValue, getSchemeTypeValue, generateSchemeStr, getSchemeStatus, roleAccess, formatCloseDateDisplay } from "./DiscountMasterList";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { AuthState } from "context/AuthContext";
import { DataGrid } from "@mui/x-data-grid";
import { toMySQLDateTime } from "./DiscountMaster";
import { DialogBox } from "./summary/dialogBox";
import LockClockIcon from "@mui/icons-material/LockClock";
import moment from "moment";


export const getStoreMatchValues = (store) =>
	[
		store?.store_name,
		store?.storeName,
		store?.ginesys_short_name,
		store?.partner_site_name,
		store?.site_code,
		store?.siteCode,
		store?.store_code,
		store?.storeCode,
		store?.code,
		store?.site_name,
		store?.name,
	]
		.map(normalizeValue)
		.filter(Boolean);

export const getStoreCodeValues = (store) =>
	[
		store?.site_code,
		store?.siteCode,
		store?.store_code,
		store?.storeCode,
		store?.code,
	]
		.map(normalizeValue)
		.filter(Boolean);

export const getSelectedStoreCodes = (data) =>
	[
		...toValueList(data?.store_code),
		...toValueList(data?.storeCode),
		...toValueList(data?.site_code),
		...toValueList(data?.siteCode),
		...toValueList(data?.store_codes),
		...toValueList(data?.storeCodes),
		...toValueList(data?.store),
	]
		.map((store) =>
			typeof store === "object"
				? store?.store_code || store?.storeCode || store?.site_code || store?.siteCode || store?.code || ""
				: store
		)
		.map((value) => String(value || "").trim())
		.filter(Boolean);

export const getStoreDisplayName = (store) =>
	store?.store_name ||
	store?.ginesys_short_name ||
	store?.storeName ||
	store?.partner_site_name ||
	store?.store_code ||
	store?.site_name ||
	store?.name ||
	"Unknown store";

export const generateColumns = (data) =>{
	let columns = []
	Object.keys(data)?.forEach((key) =>{
		const headerName = key
			.replaceAll("_", " ")
			.replace(/\b\w/g, (char) => char.toUpperCase());
		columns.push({
				field: key,
				headerName,
				align: "left",
				headerAlign: "left",
				flex: 0,
				minWidth: key.length > 12 ? 170 : 130,
				renderCell: (params) => {
					const value = params.value === null || params.value === undefined || params.value === "" ? "-" : String(params.value);
					return (
						<Tooltip title={value}>
							<Box
								sx={{
									width: "100%",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									fontSize: "10px",
									color: "#344054",
								}}
							>
								{value}
							</Box>
						</Tooltip>
					);
				}
			})
	})
	return columns
}

export const getDistinctEmails = (values) =>
	[...new Set(
		values
			.flatMap((value) =>
				Array.isArray(value)
					? value
					: String(value || "")
						.split(/[;,]/)
			)
			.map((value) => value.trim().toLowerCase())
			.filter(Boolean)
	)];

export const normalizeValue = (value) => String(value || "").trim().toLowerCase();

export const toValueList = (value) => {
	if (Array.isArray(value)) return value;
	if (typeof value === "string") return value.split(",");
	if (value) return [value];
	return [];
};

export const getComparableValue = (value) =>
	typeof value === "object"
		? value?.store_code || value?.storeCode || value?.code || value?.key || value?.name || value?.store_name || value?.ginesys_short_name || ""
		: value;

export const getFieldValue = (row, field) => {
	if (!row) return "";
	if (row[field] !== undefined) return row[field];
	const normalizeFieldKey = (value) => normalizeValue(value).replace(/_/g, "");
	const matchingKey = Object.keys(row).find((key) => normalizeFieldKey(key) === normalizeFieldKey(field));
	return matchingKey ? row[matchingKey] : "";
};

const hasTruthyField = (row, keys = []) =>
	keys.some((key) => {
		const value = row?.[key];
		if (typeof value === "string") {
			return ["true", "yes", "y", "1", "sent"].includes(value.trim().toLowerCase());
		}
		return Boolean(value);
	});

const hasPublishedEmailSent = (row) =>
	hasTruthyField(row, [
		"published_email_status",
		"published_mail_status",
		"publish_email_status",
		"publish_mail_status",
		"published_email_sent",
		"published_mail_sent",
		"publish_email_sent",
		"publish_mail_sent",
	]);

const hasClosedEmailSent = (row) =>
	hasTruthyField(row, [
		"closed_email_status",
		"closed_mail_status",
		"close_email_status",
		"close_mail_status",
		"closed_email_sent",
		"closed_mail_sent",
		"close_email_sent",
		"close_mail_sent",
	]);

const canShowForceCloseForClosedEmail = (row) => !hasClosedEmailSent(row);

const isForceClosed = (row) =>
	hasTruthyField(row, ["force_closed", "is_force_closed"]) ||
	["force closed", "closed"].includes(String(row?.status || "").trim().toLowerCase());

const getForceCloseDate = (row) =>
	row?.close_date || row?.closed_date || "";

const getForceCloseReason = (row) =>
	row?.close_reason || row?.closed_reason || row?.force_close_reason || "";

const getForceCloseRequestedBy = (row) =>
	row?.close_requested_by || row?.closed_requested_by || row?.force_close_requested_by || "";

const getForceCloseApprovedBy = (row) =>
	row?.close_approved_by ||
	row?.closed_approved_by ||
	row?.force_close_approved_by ||
	row?.approved_by ||
	row?.force_closed_by ||
	row?.closed_by ||
	"";

const getExtendedDate = (row) =>
	row?.extended_end_date || row?.extended_date || row?.new_end_date || row?.revised_end_date || "";

const getCurrentDateTime = () => moment().format("YYYY-MM-DDTHH:mm");

const parsePickerDateTime = (value) => {
	if (!value) return null;
	const date = moment(value);
	return date.isValid() ? date.toDate() : null;
};

const formatPickerDateTime = (value) => {
	if (!value) return "";
	const date = moment(value);
	return date.isValid() ? date.format("YYYY-MM-DDTHH:mm") : "";
};

const getForceCloseMaxDate = (row) => {
	const maxDateSource = getExtendedDate(row) || row?.end_date;
	if (!maxDateSource) return "";
	const endDate = moment(maxDateSource);
	return endDate.isValid() ? endDate.format("YYYY-MM-DDTHH:mm") : "";
};

const hasExplicitTime = (value) => {
	if (!value) return false;
	const match = String(value).match(/T(\d{2}):(\d{2})(?::(\d{2}))?/);
	if (!match) return false;
	const [, hour, minute, second = "00"] = match;
	return hour !== "00" || minute !== "00" || second !== "00";
};

const getEffectiveStartMoment = (value) => {
	if (!value) return null;
	const date = moment(value);
	if (!date.isValid()) return null;
	return hasExplicitTime(value) ? date : date.startOf("day");
};

const getEffectiveEndMoment = (value) => {
	if (!value) return null;
	const date = moment(value);
	if (!date.isValid()) return null;
	return hasExplicitTime(value) ? date : date.endOf("day");
};

const isDiscountCurrentlyActive = (row) => {
	if (!row?.start_date) return false;

	const startDate = getEffectiveStartMoment(row.start_date);
	const endDate = getEffectiveEndMoment(getExtendedDate(row) || row?.end_date);

	if (!startDate || !endDate) {
		return false;
	}

	const now = moment();
	return now.isSameOrAfter(startDate) && now.isSameOrBefore(endDate);
};

const DiscountSummaryPage = () => {
	const location = useLocation();
	const data = location.state?.data;

	// State for modals
	const [viewAllModal, setViewAllModal] = useState({
		open: false,
		type: null, // 'zone', 'state', 'city', 'store_grade', 'store', 'applicable_days'
	});
	const [searchTerm, setSearchTerm] = useState('');
	const [barcodeSearchTerm, setBarcodeSearchTerm] = useState('');
	const [publishLoading, setPublishLoading] = useState(false);
	const [publishStatus, setPublishStatus] = useState(null); // null, 'success', 'error'
	const [categoryData, setCategoryData] = useState([])
	const [barcodeData, setBardoceData] = useState([])
	const [open, setOpen] = useState(false)
	const [rejectOpen, setRejectOpen] = useState(false)
	const [rejectReason, setRejectReason] = useState("");
	const [forceCloseOpen, setForceCloseOpen] = useState(false);
	const [forceCloseDate, setForceCloseDate] = useState(getCurrentDateTime());
	const [forceCloseReason, setForceCloseReason] = useState("");
	const [forceCloseLoading, setForceCloseLoading] = useState(false);
	const [apiCall, setApiCall] = useState(false)
	const [paginationModel, setPaginationModel] = useState({
			page: 0,
			pageSize: 25,
		});
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();

	const navigate = useNavigate();

	const MAX_VISIBLE_ITEMS = 3;

	const roleData = roleAccess(user)

	const openForceCloseDialog = () => {
		const currentDateTime = getCurrentDateTime();
		const maxDate = getForceCloseMaxDate(data);
		const closeDate =
			maxDate && moment(currentDateTime).isAfter(moment(maxDate)) ? maxDate : currentDateTime;

		setForceCloseDate(closeDate);
		setForceCloseReason("");
		setForceCloseOpen(true);
	};

	const closeForceCloseDialog = () => {
		setForceCloseOpen(false);
		setForceCloseDate(getCurrentDateTime());
		setForceCloseReason("");
	};

	const handleGetStoreData = async (channel, brand) => {
			try {
				setLoadingOpen(true);
				const params = new URLSearchParams({
								channel: channel,
								brand: brand,
							});
							const res = await pythonVmInstance.get(`/discount-master/discount-store-master?${params.toString()}`);

				return res?.data;
			} catch (err) {
				throw new Error("Failed to fetch store master data");
			}
		};

	const handleFinalSummary = (barcodeData) => {
			let data1 = barcodeData.map(element => {
				return {...element,
					'scheme': generateSchemeStr(data?.discounts, data?.scheme_type) || '',
					'scheme_name': data?.scheme_name || '',
					'date': `${data?.start_date || ''} to ${data?.end_date || ''}`,
					'days': data?.applicable_days || [],
				}
			});
			setCategoryData(data1);
		};

	const getBarcodes = async () => {
		try {
			// setDownloading(true);
			setLoadingOpen(true);
			await pythonVmInstance({
				url: `/discount-master/get-barcodes`,params: { channel:data?.channel, barcode_url: data?.barcode_file_url, discounted_ean_url: data?.summary_file_url },
				headers: {
					"Content-Type": "application/json",
				},
				method: "GET",
			}).then((res) => {
				setBardoceData(res?.data?.barcode_details || []);
				handleFinalSummary(res?.data?.item_details || [])
				setLoadingOpen(false);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setLoadingOpen(false);
			setSnackBar({
				...snackBar,
				message: "Failed to load barcode details",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};
	const publishDiscount = async () => {
		try {
			// setDownloading(true);
			setLoadingOpen(true);

			const body = {
				published_at: toMySQLDateTime(new Date()),
				published_by: user?.email,
				status: "Published",
				// store_emails: presentEmails,
			};

			await pythonVmInstance({
				url: `/discount-master/publish/${data?.id}`,
				data: body,
				headers: {
					"Content-Type": "application/json",
				},
				method: "PATCH",
			}).then((res) => {
				setLoadingOpen(false);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message:
						res?.data?.message ||
						"Updated successfully. It will take a few minutes to receive the mail.",
					severity: "success",
					anchorOrigin: {
						horizontal: "center",
						vertical: "top",
					},
				});
				navigate(-1);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setLoadingOpen(false);

			setSnackBar({
				...snackBar,
				message: error?.response?.data?.message || "Failed to publish discount",
				severity: "error",
				anchorOrigin: {
					horizontal: "center",
					vertical: "top",
				},
			});
		}
	};
	const rejectDiscount = async () => {
		try {
			// setDownloading(true);
			setLoadingOpen(true);

			const body = {
				rejected_at: toMySQLDateTime(new Date()),
				rejected_by: user?.email,
				status: "Rejected",
				rejected_reason: rejectReason,
				// store_emails: presentEmails,
			};

			await pythonVmInstance({
				url: `/discount-master/reject/${data?.id}`,
				data: body,
				headers: {
					"Content-Type": "application/json",
				},
				method: "PATCH",
			}).then((res) => {
				setLoadingOpen(false);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Discount rejected successfully",
					severity: "success",
					anchorOrigin: {
						horizontal: "center",
						vertical: "top",
					},
				});
				navigate(-1);
			});
		} catch (error) {
			setSnackBarOpen(true);
			setLoadingOpen(false);

			setSnackBar({
				...snackBar,
				message: error?.response?.data?.message || "Failed to reject discount",
				severity: "error",
				anchorOrigin: {
					horizontal: "center",
					vertical: "top",
				},
			});
		}
	};

	const forceCloseDiscount = async () => {
		if (!forceCloseDate) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select close date",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		if (!forceCloseReason.trim()) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please enter close reason",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		const currentDateTime = moment();
		const selectedCloseDate = moment(forceCloseDate, "YYYY-MM-DDTHH:mm", true);
		const maxCloseDateSource = getExtendedDate(data) || data?.end_date;
		const endDate = maxCloseDateSource ? moment(maxCloseDateSource) : null;

		if (!selectedCloseDate.isValid()) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select a valid close date",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		if (selectedCloseDate.isBefore(currentDateTime)) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Close date cannot be before current time",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		if (endDate && selectedCloseDate.isAfter(endDate)) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Close date cannot be after scheme max end date",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		try {
			setForceCloseLoading(true);
			setLoadingOpen(true);

			const body = {
				status: "Force Closed",
				closed_at: toMySQLDateTime(new Date()),
				close_date: selectedCloseDate.format("YYYY-MM-DD HH:mm:ss"),
				closed_by: user?.email,
				close_reason: forceCloseReason.trim(),
			};

			const res = await pythonVmInstance({
				url: `/discount-master/force-close/${data?.id}`,
				data: body,
				headers: {
					"Content-Type": "application/json",
				},
				method: "PATCH",
			});

			setForceCloseLoading(false);
			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message:
					res?.data?.message ||
					"Discount closed successfully. It will take a few minutes to receive the mail.",
				severity: "success",
				anchorOrigin: {
					horizontal: "center",
					vertical: "top",
				},
			});
			closeForceCloseDialog();
			navigate(-1);
		} catch (error) {
			setForceCloseLoading(false);
			setLoadingOpen(false);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: error?.response?.data?.message || "Failed to force close discount",
				severity: "error",
				anchorOrigin: {
					horizontal: "center",
					vertical: "top",
				},
			});
		}
	};

	useEffect(() =>{
		getBarcodes()
		// barcodeDetails()
	}, [])
	// Helper function to determine scheme status based on dates and publish status

	// Generate scheme descriptions for display
	const generateSchemeDescriptions = () => {
		if (!data?.discounts || data.discounts.length === 0) {
			return [];
		}
		const schemes = [];
		data.discounts.forEach((element) => {
			const rowScheme = getSchemeTypeValue(element?.scheme_type, getSchemeTypeValue(data.scheme_type));
			const getValue = rowScheme.includes("%") ? element.percent : rowScheme.includes("Amount_Off") ? element.value : element.get;
			if (rowScheme === "Flat_Discount_%") {
				schemes.push(`Flat ${element.percent}% Discount`);
			} else if (rowScheme === "Flat_Discount_Amount" || rowScheme === "Flat_Discount_Value") {
				schemes.push(`Flat ₹${element.value} Off`);
			} else if (rowScheme.includes("product_@_Amount") || rowScheme.includes("product @ Amount")) {
				schemes.push(`Buy For ${element.buy} & Get ${element.product || "product"} @ ₹${element.value}`);
			} else if (rowScheme === "NOD") {
				schemes.push("NOD");
			} else {
				schemes.push(formatSchemeText(formatSchemeTypeValue(rowScheme), element.buy, getValue));
			}
		});
		return schemes;
	};


	// Helper function to render items with "View All" option
	const renderItemsWithViewAll = (items, type, colors) => {
		if (!items || items.length === 0) {
			return <Typography sx={{ fontWeight: '700', color: '#212121', fontSize: '16px' }}>—</Typography>;
		}

		const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS);
		const hiddenCount = items.length - MAX_VISIBLE_ITEMS;

		return (
			<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: '8px' }}>
				{visibleItems.map((item, idx) => (
					<Chip
						key={idx}
						label={item}
						size="small"
						sx={colors}
					/>
				))}
				{hiddenCount > 0 && (
					<Chip
						label={`+${hiddenCount} more`}
						size="small"
						onClick={() => {
							setViewAllModal({ open: true, type });
							setSearchTerm('');
						}}
						sx={{
							background: '#212121',
							color: '#ffffff',
							fontWeight: '700',
							fontSize: '11px',
							cursor: 'pointer',
							'&:hover': { background: '#3a3a3a' },
						}}
					/>
				)}
			</Stack>
		);
	};

	// Filter items based on search
	const filteredItems = viewAllModal.type === 'scheme_type'
		? generateSchemeDescriptions().filter((item) =>
				item.toLowerCase().includes(searchTerm.toLowerCase())
			)
		: viewAllModal.type
		? (data?.[viewAllModal.type] || []).filter((item) =>
				item.toLowerCase().includes(searchTerm.toLowerCase())
			)
		: [];

	// Filter barcodes based on search
	const filteredBarcodes = (barcodeData || []).filter((barcode) =>
	(barcode.barcode || "")
		.toLowerCase()
		.includes(barcodeSearchTerm.toLowerCase())
);

	// Export barcodes to CSV
	const exportToCSV = () => {
		if (!barcodeData.length) return;

		const headers = Object.keys(barcodeData[0]).join(",");
		const rows = barcodeData.map(obj =>
			Object.values(obj)
			.map(val => `"${val}"`)
			.join(",")
		);

		const csvContent = [headers, ...rows].join("\n");

		const blob = new Blob([csvContent], { type: "text/csv" });
		const url = window.URL.createObjectURL(blob);

		let filename = `${data.scheme_name}.csv`

		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		a.click();
	};

	const rowsWithId = filteredBarcodes && filteredBarcodes.map((row, index) => ({
  id: index, // or index + 1
  ...row,
}));

	const selectedStartDate = data?.start_date ? new Date(data.start_date) : null;
	const today = new Date();
	const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	const publishStartDate = selectedStartDate
		? new Date(
			selectedStartDate.getFullYear(),
			selectedStartDate.getMonth(),
			selectedStartDate.getDate()
		)
		: null;

	const canShowActions =
		["PUBLISHER", "ADMIN"].includes(roleData.roles) &&
		data?.status !== "Published" &&
		barcodeData.length > 0 &&
		data?.status !== "Rejected";

	const isPublishBlockedByDate = !publishStartDate || publishStartDate < todayStart;
	const disablePublishButton = isPublishBlockedByDate;
	const publishTooltipTitle = isPublishBlockedByDate
		? "Publish is allowed only when the discount start date is today or later."
		: "";
	const rejectTooltipTitle = isPublishBlockedByDate
		? "Reject is allowed only when the discount start date is today or later."
		: "";
	const schemeStatus = getSchemeStatus(data);
	const isClosedDiscount = isForceClosed(data);
	const forceCloseDateValue = getForceCloseDate(data);
	const forceCloseReasonValue = getForceCloseReason(data);
	const forceCloseRequestedByValue = getForceCloseRequestedBy(data);
	const forceCloseApprovedByValue = getForceCloseApprovedBy(data);
	const canShowForceClose =
		["PUBLISHER", "ADMIN"].includes(roleData.roles) &&
		data?.status === "Published" &&
		isDiscountCurrentlyActive(data) &&
		canShowForceCloseForClosedEmail(data) &&
		!isClosedDiscount;

	return (
		<Box className="discount-container">
			<Box className="discount-max-width">
				{/* Success Message */}
				{publishStatus === 'success' && (
					<Box sx={{
						background: '#e8f5e9',
						border: '1px solid #4caf50',
						borderRadius: '8px',
						padding: '14px 16px',
						marginBottom: '20px',
						display: 'flex',
						alignItems: 'center',
						gap: 1,
					}}>
						<Typography sx={{ fontSize: '20px' }}>✅</Typography>
						<Box>
							<Typography sx={{ fontWeight: '700', color: '#2e7d32', fontSize: '13px' }}>
								Scheme Published Successfully!
							</Typography>
							<Typography sx={{ fontSize: '12px', color: '#558b2f', mt: 0.3 }}>
								{data?.scheme_name} is now live and active
							</Typography>
						</Box>
					</Box>
				)}

				{/* Header Section */}
				<Box className="discount-header-section">
					<Typography className="discount-scheme-title">
						{data?.scheme_name || 'Discount Scheme'}
					</Typography>

				<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25,mb: 0.75, gap: '8px', display: 'flex', justifyContent: 'space-between' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Chip
							label={schemeStatus.label}
							size="small"
							sx={{
								background: schemeStatus.bgColor,
								color: schemeStatus.color,
								border: `1.5px solid ${schemeStatus.color}`,
								fontWeight: '600',
								fontSize: '10px',
								height: 22,
							}}
						/>
						{data?.status === "Rejected" && <Typography sx={{ fontSize: '10px' }}>- {data?.rejected_reason} </Typography>}
					</Box>
					</Stack>

					<Typography className="discount-date-range">
						{data?.start_date && data?.end_date
							? `${new Date(data.start_date).toLocaleDateString('en-US', {
									year: 'numeric',
									month: 'short',
									day: 'numeric',
							})} ${new Date(data.start_date).toLocaleTimeString('en-US', {
								hour: '2-digit',
								minute: '2-digit',
								hour12: true,
							})} — ${new Date(data.end_date).toLocaleDateString('en-US', {
								year: 'numeric',
								month: 'short',
								day: 'numeric',
							})} ${new Date(data.end_date).toLocaleTimeString('en-US', {
								hour: '2-digit',
								minute: '2-digit',
								hour12: true,
								})}`
							: 'Date range not available'}
					</Typography>
				</Box>

				{/* Stats Grid */}
				<Box className="discount-stats-grid">
				<Box className="discount-stat-card">
					<Typography className="discount-stat-label">Brand</Typography>
					<Typography className="discount-stat-value">{data?.brand || '—'}</Typography>
				</Box>

				<Box className="discount-stat-card">
					<Typography className="discount-stat-label">Channel</Typography>
					<Typography className="discount-stat-value">{data?.channel || '—'}</Typography>
					</Box>

				<Box className="discount-stat-card">
					<Typography className="discount-stat-label">Scheme Type</Typography>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
						{data?.discounts?.map((discount, idx)=>
						<Chip
							key={idx}
							label={discount?.display_string}
							size="small"
							sx={{background: '#ffe0b2', color: '#e65100', fontWeight: '600', fontSize: '12px'}}
						/>
						)}
					</Box>
				</Box>

				<Box className="discount-stat-card">
					<Typography className="discount-stat-label">Total Rules</Typography>
					<Typography className="discount-stat-value">{data?.discounts?.length || 0}</Typography>
				</Box>
			</Box>

			{/* Details Grid */}
			<Grid container spacing={1.25} className="discount-content-grid">
				<Grid item xs={12} sm={6} md={3}>
					<Box className="discount-info-card">
						<Typography className="discount-stat-label">Zone</Typography>
						{renderItemsWithViewAll(data?.zone, 'zone', {
							background: '#e3f2fd',
							color: '#1565c0',
							fontWeight: '600',
							fontSize: '10px',
						})}
					</Box>
				</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<Box className="discount-info-card">
							<Typography className="discount-stat-label">State</Typography>
							{renderItemsWithViewAll(data?.state, 'state', {
								background: '#f3e5f5',
								color: '#6a1b9a',
								fontWeight: '600',
								fontSize: '10px',
							})}
						</Box>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
					<Box className="discount-info-card">
						<Typography className="discount-stat-label">City</Typography>
							{renderItemsWithViewAll(data?.city, 'city', {
								background: '#e8f5e9',
								color: '#2e7d32',
								fontWeight: '600',
								fontSize: '10px',
							})}
						</Box>
					</Grid>

				<Grid item xs={12} sm={6} md={3}>
				<Box className="discount-info-card">
					<Typography className="discount-stat-label">Store Grade</Typography>
						{renderItemsWithViewAll(data?.store_grade, 'store_grade', {
							background: '#fff3e0',
							color: '#e65100',
							fontWeight: '600',
							fontSize: '10px',
						})}
					</Box>
				</Grid>

				{data?.distributor?.length > 0 && (
					<Grid item xs={12} sm={6} md={4}>
						<Box className="discount-info-card">
							<Typography className="discount-stat-label">Distributor</Typography>
							{renderItemsWithViewAll(data?.distributor, 'distributor', {
								background: '#e0f2f1',
								color: '#00695c',
								fontWeight: '600',
								fontSize: '10px',
							})}
						</Box>
					</Grid>
				)}

				{data?.partner?.length > 0 && (
					<Grid item xs={12} sm={6} md={4}>
						<Box className="discount-info-card">
							<Typography className="discount-stat-label">Partner</Typography>
							{renderItemsWithViewAll(data?.partner, 'partner', {
								background: '#fce4ec',
								color: '#ad1457',
								fontWeight: '600',
								fontSize: '10px',
							})}
						</Box>
					</Grid>
				)}

				<Grid item xs={12} sm={6} md={4}>
				<Box className="discount-info-card">
					<Typography className="discount-stat-label">Store Name</Typography>
						{renderItemsWithViewAll(data?.store, 'store', {
							background: '#e8e8e8',
							color: '#212121',
							fontWeight: '600',
							fontSize: '10px',
						})}
					</Box>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
				<Box className="discount-info-card">
					<Typography className="discount-stat-label">Applicable Days</Typography>
						{renderItemsWithViewAll(data?.applicable_days, 'applicable_days', {
							background: '#f5f5f5',
							color: '#212121',
							border: '1px solid #e0e0e0',
							fontWeight: '600',
							fontSize: '10px',
						})}
					</Box>
				</Grid>

				<Grid item xs={12} sm={6} md={4}>
					<Box className="discount-info-card">
						<Typography className="discount-stat-label">Submitted By</Typography>
						<Typography sx={{fontWeight: '700', color: '#212121', fontSize: '11px', wordBreak: 'break-word'}}>
							{data?.submitted_by || '—'}
						</Typography>
					</Box>
				</Grid>
				<Grid item xs={12} sm={6} md={4}>
					<Box className="discount-info-card">
						<Typography className="discount-stat-label">Published By/ Rejected By</Typography>
						<Typography sx={{fontWeight: '700', color: '#212121', fontSize: '11px', wordBreak: 'break-word'}}>
							{data?.published_by ? data?.published_by : data?.rejected_by || '—'}
						</Typography>
					</Box>
				</Grid>
				{isClosedDiscount && forceCloseDateValue && (
					<Grid item xs={12} sm={6} md={4}>
						<Box className="discount-info-card">
							<Typography className="discount-stat-label">Close Date</Typography>
							<Typography sx={{fontWeight: '700', color: '#212121', fontSize: '11px', wordBreak: 'break-word'}}>
								{formatCloseDateDisplay(forceCloseDateValue) || forceCloseDateValue}
							</Typography>
						</Box>
					</Grid>
				)}
				{isClosedDiscount && forceCloseReasonValue && (
					<Grid item xs={12} sm={6} md={4}>
						<Box className="discount-info-card">
							<Typography className="discount-stat-label">Close Reason</Typography>
							<Typography sx={{fontWeight: '700', color: '#212121', fontSize: '11px', wordBreak: 'break-word'}}>
								{forceCloseReasonValue}
							</Typography>
						</Box>
					</Grid>
				)}
				{/* {isClosedDiscount && (
					<Grid item xs={12} sm={6} md={4}>
						<Box className="discount-info-card">
							<Typography className="discount-stat-label">Close Requested By</Typography>
							<Typography sx={{fontWeight: '700', color: '#212121', fontSize: '11px', wordBreak: 'break-word'}}>
								{forceCloseRequestedByValue || '—'}
							</Typography>
						</Box>
					</Grid>
				)}
				{isClosedDiscount && (
					<Grid item xs={12} sm={6} md={4}>
						<Box className="discount-info-card">
							<Typography className="discount-stat-label">Close Approved By</Typography>
							<Typography sx={{fontWeight: '700', color: '#212121', fontSize: '11px', wordBreak: 'break-word'}}>
								{forceCloseApprovedByValue || '—'}
							</Typography>
						</Box>
					</Grid>
				)} */}
			</Grid>

			{/* Category Data Table */}
		{/* <Box className="discount-section">
			<Typography className="discount-section-title">
				Category Based Data
			</Typography>

			<FinalSummary data = {categoryData} />
		</Box> */}

			{/* Barcodes Details Table */}
		<Box className="discount-section" sx={{ mt: 2 }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25, gap: 1.25 }}>
				<Typography className="discount-section-title" sx={{ mb: 0, flex: 1 }}>
					Barcodes Details
				</Typography>
				<Button
					size="small"
					variant="outlined"
					onClick={exportToCSV}
					disabled={barcodeData.length === 0}
					sx={{
						fontSize: '10px',
						padding: '4px 10px',
						borderColor: '#212121',
						color: '#212121',
						fontWeight: '600',
						minHeight: 28,
						whiteSpace: 'nowrap',
						'&:hover': {
							background: '#f5f5f5',
							borderColor: '#212121',
						}
					}}
				>
					📥 Export CSV
				</Button>
			</Box>

			<Box sx={{ mb: 1.25 }}>
				<TextField
					placeholder="Search by Barcode..."
					fullWidth
					size="small"
					value={barcodeSearchTerm}
					onChange={(e) => setBarcodeSearchTerm(e.target.value)}
					sx={{
						background: '#ffffff',
						'& .MuiOutlinedInput-root': {
							fontSize: '11px',
							minHeight: 34,
						}
					}}
				/>
			</Box>
			<Stack sx={{ height: 460, width: '100%'}}>
				<DataGrid
					rows={rowsWithId}
					columns={generateColumns(
						barcodeData.length > 0 ? barcodeData[0] : []
					)}
					disableColumnMenu
					disableRowSelectionOnClick
					experimentalFeatures={{ newEditingApi: true }}
					loading={apiCall}
					pagination
					rowHeight={38}
					pageSizeOptions={[10, 25, 50, 100]}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					sx={{
						border: 'none',
						fontSize: '11px',
						'& .MuiDataGrid-columnHeaders': {
							minHeight: '38px !important',
							maxHeight: '38px !important',
							backgroundColor: '#f8fafc',
							borderBottom: '1px solid #e4e7ec',
						},
						'& .MuiDataGrid-columnHeaderTitle': {
							fontSize: '10px',
							fontWeight: 700,
							color: '#344054',
						},
						'& .MuiDataGrid-columnHeader': {
							paddingX: '8px',
						},
						'& .MuiDataGrid-cell': {
							fontSize: '10px',
							paddingY: '2px',
							paddingX: '8px',
							alignItems: 'center',
						},
						'& .summary-grid-header': {
							backgroundColor: '#f5f5f5',
							fontWeight: '700',
						},
					}}
				/>
			</Stack>

			
			</Box>

			{/* Action Buttons */}
			{canShowActions && <Box className="discount-action-section">
				<Tooltip title={rejectTooltipTitle}>
					<span>
						<Button 
							variant="contained" 
							color="error"
							// className="discount-cancel-btn"
							onClick={() => setRejectOpen(true)}
							disabled={disablePublishButton}
							sx={{
								fontSize: '10px',
								minHeight: 30,
								padding: '5px 14px',
								borderRadius: '7px',
								textTransform: 'none',
							}}
						>
							Reject
						</Button>
					</span>
				</Tooltip>
				<Tooltip title={publishTooltipTitle}>
					<span>
						<Button 
							variant="contained"
							className="discount-publish-btn"
							disabled={disablePublishButton}
							onClick={() => setOpen(true)}
							sx={{
								position: 'relative',
								fontSize: '10px',
								minHeight: 30,
								padding: '5px 14px',
								borderRadius: '7px',
								textTransform: 'none',
							}}
						>
							Publish
						</Button>
					</span>
				</Tooltip>
			</Box>}

			{canShowForceClose && (
				<Box className="discount-action-section">
					<Button
						variant="contained"
						color="error"
						startIcon={
							forceCloseLoading ? <CircularProgress size={16} color="inherit" /> : <LockClockIcon />
						}
						onClick={openForceCloseDialog}
						disabled={forceCloseLoading}
						sx={{
							fontSize: '10px',
							minHeight: 30,
							padding: '5px 14px',
							borderRadius: '7px',
							textTransform: 'none',
						}}
					>
						Force Close
					</Button>
				</Box>
			)}

			<DialogBox open={open} setOpen={setOpen} body={"Publishing will notify stores via email.This action is irreversible. Do you want to continue?"} handleOK={publishDiscount} />

			{/* View Rejected Modal */}
			<Dialog
				open={rejectOpen}
				onClose={() => setRejectOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					Reject Discount
				</DialogTitle>

				<DialogContent>

					<Typography sx={{ mb: 2 }}>
						Rejecting this discount request will stop further processing. Please provide the reason for rejection.
					</Typography>

					<TextField
						fullWidth
						multiline
						rows={4}
						label="Reject Reason"
						placeholder="Enter reject reason"
						value={rejectReason}
						onChange={(e) => setRejectReason(e.target.value)}
					/>

				</DialogContent>

				<DialogActions>

					<Button
						onClick={() => setRejectOpen(false)}
					>
						Cancel
					</Button>

					<Button
						color="error"
						variant="contained"
						onClick={() => {

							if (!rejectReason.trim()) {
								alert("Please enter reject reason");
								return;
							}
							rejectDiscount()

							// rejectDiscount(rejectReason);
						}}
					>
						Reject
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={forceCloseOpen}
				onClose={closeForceCloseDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					Force Close Discount
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ mb: 2 }}>
						Please select when this discount should be closed and enter the reason for force close.
					</Typography>

					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DateTimePicker
							label="Close Date & Time"
							ampm
							ampmInClock
							format="dd-MM-yyyy hh:mm a"
							value={parsePickerDateTime(forceCloseDate)}
							onChange={(newValue) => setForceCloseDate(formatPickerDateTime(newValue))}
							minDateTime={parsePickerDateTime(getCurrentDateTime())}
							maxDateTime={parsePickerDateTime(getForceCloseMaxDate(data)) || undefined}
							timeSteps={{ hours: 1, minutes: 1 }}
							slotProps={{
								textField: {
									fullWidth: true,
									sx: { mb: 2 },
								},
							}}
						/>
					</LocalizationProvider>

					<TextField
						fullWidth
						multiline
						rows={4}
						label="Reason"
						placeholder="Enter reason for force close"
						value={forceCloseReason}
						onChange={(e) => setForceCloseReason(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeForceCloseDialog}>
						Cancel
					</Button>

					<Button
						color="error"
						variant="contained"
						onClick={forceCloseDiscount}
						disabled={forceCloseLoading}
					>
						{forceCloseLoading ? <CircularProgress size={16} color="inherit" /> : "Force Close"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* View All Modal */}
			<Dialog
				open={viewAllModal.open}
				onClose={() => setViewAllModal({ open: false, type: null })}
				maxWidth="sm"
				fullWidth
			>
				<Box sx={{ background: '#f5f5f5', p: 2, borderBottom: '1px solid #e0e0e0' }}>
					<Typography sx={{ fontWeight: '700', color: '#212121', fontSize: '16px', mb: 0.5 }}>
						All {viewAllModal.type === 'applicable_days' ? 'Days' : (viewAllModal.type === 'scheme_type' ? 'Scheme Types' : (viewAllModal.type?.toUpperCase().replace(/_/g, ' ') || ''))}
					</Typography>
					<Typography sx={{ fontSize: '12px', color: '#666666', fontWeight: '500' }}>
						Total: {viewAllModal.type === 'scheme_type' ? generateSchemeDescriptions().length : (data?.[viewAllModal.type]?.length || 0)} items
					</Typography>
				</Box>
				<DialogContent sx={{ pt: 3 }}>
					<TextField
						autoFocus
						placeholder="Search items..."
						fullWidth
						size="small"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						sx={{ mb: 2 }}
					/>
					<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: '8px' }}>
						{filteredItems.length > 0 ? (
							filteredItems.map((item, idx) => (
								<Chip
									key={idx}
									label={item}
									size="medium"
									sx={{
										background: '#212121',
										color: '#ffffff',
										fontWeight: '600',
										cursor: 'pointer',
										'&:hover': { background: '#3a3a3a' },
									}}
								/>
							))
						) : (
							<Typography sx={{ color: '#999', py: 2 }}>
								No items found
							</Typography>
						)}
					</Stack>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={() => setViewAllModal({ open: false, type: null })}>Close</Button>
				</DialogActions>
			</Dialog>
		</Box>
		</Box>
	);
};

export default DiscountSummaryPage;
